(() => {
  if (customElements.get('collection-directory')) return;

  class CollectionDirectory extends HTMLElement {
    constructor() {
      super();
      this.abortController = null;
      this.resizeFrame = null;
      this.renderFrame = null;
      this.expandedSubgroups = new Set();
      this.isInitialRender = true;
      this.collator = new Intl.Collator(document.documentElement.lang || 'en', {
        numeric: true,
        sensitivity: 'base'
      });
    }

    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.abortController = new AbortController();
      this.cards = Array.from(this.querySelectorAll('[data-directory-card]'));
      this.lists = Array.from(this.querySelectorAll('[data-directory-list]'));
      this.subgroups = Array.from(this.querySelectorAll('[data-directory-subgroup]'));
      this.groups = Array.from(this.querySelectorAll('[data-directory-group]'));
      this.searchInput = this.querySelector('[data-directory-search]');
      this.sortSelect = this.querySelector('[data-directory-sort]');
      this.status = this.querySelector('[data-directory-status]');
      this.emptyState = this.querySelector('[data-directory-empty]');
      this.clearButton = this.querySelector('[data-clear-filters]');

      const requestedSort = this.dataset.defaultSort || 'manual';
      const availableSorts = this.sortSelect
        ? Array.from(this.sortSelect.options, (option) => option.value)
        : ['manual'];

      this.state = {
        area: 'all',
        letter: 'all',
        query: '',
        sort: availableSorts.includes(requestedSort) ? requestedSort : 'manual',
        view: this.dataset.defaultView === 'list' ? 'list' : 'grid'
      };

      if (this.sortSelect) this.sortSelect.value = this.state.sort;
      this.bindEvents();
      this.applyView();
      this.render();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      if (this.resizeFrame) cancelAnimationFrame(this.resizeFrame);
      if (this.renderFrame) cancelAnimationFrame(this.renderFrame);
      this.dataset.initialized = 'false';
    }

    bindEvents() {
      const { signal } = this.abortController;

      this.searchInput?.addEventListener('input', (event) => {
        this.state.query = this.normalize(event.currentTarget.value.trim());
        this.expandedSubgroups.clear();
        this.scheduleRender();
      }, { signal });

      this.sortSelect?.addEventListener('change', (event) => {
        this.state.sort = event.currentTarget.value;
        this.render();
      }, { signal });

      this.addEventListener('click', (event) => {
        const areaButton = event.target.closest('[data-area-filter]');
        if (areaButton) {
          this.state.area = areaButton.dataset.areaFilter;
          this.expandedSubgroups.clear();
          this.render();
          return;
        }

        const letterButton = event.target.closest('[data-letter-filter]');
        if (letterButton && !letterButton.disabled) {
          this.state.letter = letterButton.dataset.letterFilter;
          this.expandedSubgroups.clear();
          this.render();
          return;
        }

        const viewButton = event.target.closest('[data-view]');
        if (viewButton) {
          this.state.view = viewButton.dataset.view;
          this.expandedSubgroups.clear();
          this.applyView();
          this.render();
          return;
        }

        const showMoreButton = event.target.closest('[data-show-more]');
        if (showMoreButton) {
          const subgroup = showMoreButton.closest('[data-directory-subgroup]');
          const key = subgroup?.dataset.directorySubgroup;
          if (!key) return;

          if (this.expandedSubgroups.has(key)) {
            this.expandedSubgroups.delete(key);
          } else {
            this.expandedSubgroups.add(key);
          }
          this.render();
          return;
        }

        if (event.target.closest('[data-clear-filters], [data-empty-clear]')) {
          this.clearFilters();
        }
      }, { signal });

      window.addEventListener('resize', () => {
        if (this.resizeFrame) cancelAnimationFrame(this.resizeFrame);
        this.resizeFrame = requestAnimationFrame(() => this.render());
      }, { signal, passive: true });

      document.addEventListener('shopify:block:select', (event) => {
        if (!this.contains(event.target)) return;
        this.clearFilters(false);
        event.target.hidden = false;
        event.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, { signal });
    }

    scheduleRender() {
      if (this.renderFrame) cancelAnimationFrame(this.renderFrame);
      this.renderFrame = requestAnimationFrame(() => {
        this.renderFrame = null;
        this.render();
      });
    }

    normalize(value) {
      return String(value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLocaleLowerCase(document.documentElement.lang || 'en');
    }

    clearFilters(focusSearch = true) {
      this.state.area = 'all';
      this.state.letter = 'all';
      this.state.query = '';
      this.expandedSubgroups.clear();
      if (this.searchInput) {
        this.searchInput.value = '';
        if (focusSearch) this.searchInput.focus({ preventScroll: true });
      }
      this.render();
    }

    applyView() {
      this.classList.toggle('tvc-directory--view-grid', this.state.view === 'grid');
      this.classList.toggle('tvc-directory--view-list', this.state.view === 'list');

      this.querySelectorAll('[data-view]').forEach((button) => {
        const active = button.dataset.view === this.state.view;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });
    }

    cardMatches(card, ignoreLetter = false) {
      const areaMatches = this.state.area === 'all' || card.dataset.area === this.state.area;
      const searchableText = this.normalize(`${card.dataset.title} ${card.dataset.handle}`);
      const queryMatches = !this.state.query || searchableText.includes(this.state.query);
      const letterMatches = ignoreLetter || this.state.letter === 'all' || card.dataset.letter === this.state.letter;
      return areaMatches && queryMatches && letterMatches;
    }

    sortCards() {
      const compare = (cardA, cardB) => {
        switch (this.state.sort) {
          case 'title-asc':
            return this.collator.compare(cardA.dataset.title, cardB.dataset.title);
          case 'title-desc':
            return this.collator.compare(cardB.dataset.title, cardA.dataset.title);
          case 'count-desc':
            return Number(cardB.dataset.count) - Number(cardA.dataset.count)
              || this.collator.compare(cardA.dataset.title, cardB.dataset.title);
          case 'count-asc':
            return Number(cardA.dataset.count) - Number(cardB.dataset.count)
              || this.collator.compare(cardA.dataset.title, cardB.dataset.title);
          case 'manual':
          default:
            return Number(cardA.dataset.order) - Number(cardB.dataset.order)
              || this.collator.compare(cardA.dataset.title, cardB.dataset.title);
        }
      };

      this.lists.forEach((list) => {
        Array.from(list.querySelectorAll('[data-directory-card]'))
          .sort(compare)
          .forEach((card) => list.append(card));
      });
    }

    getColumnCount() {
      const width = window.innerWidth;
      if (this.state.view === 'list') {
        if (width < 750) return 1;
        if (width < 990) return 2;
        return Math.max(1, Number(this.dataset.listColumns) || 3);
      }

      if (width < 750) return Math.max(1, Number(this.dataset.columnsMobile) || 1);
      if (width < 990) return Math.max(1, Number(this.dataset.columnsTablet) || 3);
      return Math.max(1, Number(this.dataset.columnsDesktop) || 4);
    }

    updateAlphabetAvailability() {
      const availableLetters = new Set(
        this.cards
          .filter((card) => this.cardMatches(card, true))
          .map((card) => card.dataset.letter)
      );

      this.querySelectorAll('[data-letter-filter]').forEach((button) => {
        const letter = button.dataset.letterFilter;
        if (letter !== 'all') button.disabled = !availableLetters.has(letter);
        const active = letter === this.state.letter;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });
    }

    render() {
      if (!this.cards) return;
      this.sortCards();
      this.syncFilterControls();
      this.updateAlphabetAvailability();

      const initialRows = Math.max(0, Number(this.dataset.initialRows) || 0);
      const columns = this.getColumnCount();
      const capacity = initialRows * columns;
      const filteredMode = Boolean(this.state.query) || this.state.letter !== 'all';
      const areaTotals = { wine: 0, provisions: 0 };
      let matchingTotal = 0;

      this.subgroups.forEach((subgroup) => {
        const subgroupKey = subgroup.dataset.directorySubgroup;
        const subgroupCards = Array.from(subgroup.querySelectorAll('[data-directory-card]'));
        const matchingCards = subgroupCards.filter((card) => this.cardMatches(card));
        const expanded = this.expandedSubgroups.has(subgroupKey);
        const shouldLimit = initialRows > 0 && !filteredMode && !expanded;
        const hasOverflow = initialRows > 0 && !filteredMode && matchingCards.length > capacity;

        matchingCards.forEach((card) => {
          if (card.dataset.area in areaTotals) areaTotals[card.dataset.area] += 1;
        });
        matchingTotal += matchingCards.length;

        subgroupCards.forEach((card) => {
          const matches = matchingCards.includes(card);
          const matchIndex = matches ? matchingCards.indexOf(card) : -1;
          const limitedOut = matches && shouldLimit && matchIndex >= capacity;
          const wasHidden = card.hidden;
          card.hidden = !matches || limitedOut;

          if (!card.hidden && (this.isInitialRender || wasHidden)) {
            card.classList.remove('is-entering');
            void card.offsetWidth;
            card.classList.add('is-entering');
          }
        });

        subgroup.hidden = matchingCards.length === 0;
        const count = subgroup.querySelector('[data-subgroup-count]');
        if (count) count.textContent = String(matchingCards.length);

        const showMore = subgroup.querySelector('[data-show-more]');
        if (showMore) {
          showMore.hidden = !hasOverflow && !expanded;
          showMore.setAttribute('aria-expanded', String(expanded));
          if (expanded) {
            showMore.childNodes[0].nodeValue = 'Show less ';
            const hiddenCount = showMore.querySelector('[data-hidden-count]');
            if (hiddenCount) hiddenCount.textContent = '';
          } else {
            showMore.childNodes[0].nodeValue = 'View all ';
            const hiddenCount = showMore.querySelector('[data-hidden-count]');
            if (hiddenCount) hiddenCount.textContent = hasOverflow ? `(+${matchingCards.length - capacity})` : '';
          }
        }
      });

      this.groups.forEach((group) => {
        const area = group.dataset.directoryGroup;
        const areaAllowed = this.state.area === 'all' || this.state.area === area;
        group.hidden = !areaAllowed || areaTotals[area] === 0;
        const count = group.querySelector(`[data-group-count="${area}"]`);
        if (count) count.textContent = String(areaTotals[area]);
      });

      const filterActive = this.state.area !== 'all' || this.state.letter !== 'all' || Boolean(this.state.query);
      if (this.clearButton) this.clearButton.hidden = !filterActive;
      if (this.emptyState) this.emptyState.hidden = matchingTotal !== 0;
      if (this.status) {
        const noun = matchingTotal === 1 ? 'collection' : 'collections';
        this.status.textContent = filterActive
          ? `${matchingTotal} ${noun} found`
          : `${matchingTotal} ${noun}`;
      }

      this.isInitialRender = false;
    }

    syncFilterControls() {
      this.querySelectorAll('[data-area-filter]').forEach((button) => {
        const active = button.dataset.areaFilter === this.state.area;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });
    }
  }

  customElements.define('collection-directory', CollectionDirectory);
})();
